import { venueZone } from "../format.js";
import { useCallback, useEffect, useState } from "react";
import { EXPORT_KEEP_DAYS, type ExportJob } from "@baros/core";
import { useApp } from "../context.js";
import { request } from "./api.js";

/** A job as the API shows it: whether its archive has gone, or it has stalled. */
type ShownJob = ExportJob & { readonly expired: boolean; readonly stuck: boolean };

const exportsApi = {
  list: (token: string) => request<{ exports: readonly ShownJob[] }>(token, "GET", "/v1/org/exports"),
  start: (token: string) => request<{ export: ShownJob }>(token, "POST", "/v1/org/exports"),
  link: (token: string, exportId: string) =>
    request<{ url: string }>(token, "GET", `/v1/org/exports/${encodeURIComponent(exportId)}/download`),
};

function size(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function when(iso: string): string {
  return new Date(iso).toLocaleString("en-AU", { timeZone: venueZone(), day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
}

/**
 * Screen 12 › Data: a copy of everything the organisation has put into
 * Bar OS, made on request (#84). The archive is built in the background, so
 * the list polls while one is being made and offers the download when it is
 * ready.
 */
export function DataExport() {
  const app = useApp();
  const [jobs, setJobs] = useState<readonly ShownJob[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setJobs((await exportsApi.list(app.token)).exports);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not read the exports");
    }
  }, [app.token]);

  useEffect(() => {
    void load();
  }, [load]);

  // While an archive is being made, look again every few seconds.
  const making = (jobs ?? []).some((j) => (j.status === "queued" || j.status === "running") && !j.stuck);
  useEffect(() => {
    if (!making) return;
    const timer = window.setInterval(() => void load(), 4000);
    return () => window.clearInterval(timer);
  }, [making, load]);

  return (
    <div className="org-section">
      <h3>Your data</h3>
      <div className="org-note">
        A copy of everything this organisation has put into Bar OS, in one zip: every recipe with its
        version history, ingredients, suppliers, stock and counts, prep, wastage, orders, expenses and
        their slips, cash-ups, till sales, the development chats and the activity log. Spreadsheets you can open as they
        are, and the complete records as JSON. Archives are kept for {EXPORT_KEEP_DAYS} days.
      </div>

      <div className="row int-actions">
        <button
          className="btn"
          disabled={busy || making}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              await exportsApi.start(app.token);
              await load();
            } catch (err) {
              setError(err instanceof Error ? err.message : "Could not start the export");
            } finally {
              setBusy(false);
            }
          }}
        >
          {making ? "Making the archive…" : busy ? "Starting…" : "Export everything"}
        </button>
      </div>

      {error && <div className="msg err">{error}</div>}
      {jobs === null && !error && <div className="label">Reading the exports…</div>}
      {jobs?.length === 0 && <div className="org-note">No exports yet.</div>}

      {jobs && jobs.length > 0 && (
        <div className="table">
          {jobs.map((j) => (
            <div className="trow org-trow export-row" key={j.exportId}>
              <span className="m-name">
                <span className="n">{when(j.requestedAt)}</span>
                <span className="s">
                  {j.status === "ready"
                    ? `${size(j.bytes)} · ${j.records.toLocaleString("en-AU")} records · ${j.attachments} slips`
                    : j.status === "failed"
                      ? `Failed: ${j.error ?? "unknown reason"}`
                      : j.stuck
                        ? "Stopped without finishing; make a new one"
                        : j.status === "running"
                          ? "Being made…"
                          : "Waiting to start…"}
                </span>
              </span>
              <span className="spacer" />
              {j.status === "ready" && !j.expired && (
                <button
                  className="btn sm"
                  onClick={async () => {
                    try {
                      const { url } = await exportsApi.link(app.token, j.exportId);
                      window.location.assign(url);
                    } catch (err) {
                      setError(err instanceof Error ? err.message : "Could not get the download");
                    }
                  }}
                >
                  Download
                </button>
              )}
              {j.expired && <span className="label">Cleared after {EXPORT_KEEP_DAYS} days</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
